<template>
  <div>
    <div
      v-if="error"
      class="alert alert-danger d-flex align-items-center gap-2 m-3"
    >
      <i class="bi bi-exclamation-triangle-fill" />
      <span>{{ error }}</span>
    </div>

    <!-- Bramy -->
    <div class="assets-grid-card">
      <div class="assets-grid-card__header">
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-hdd-network" />
          </div>
          <div>
            <div class="assets-grid-title__text">
              {{ $t('components.infrastructure.dataCleanup.gatewaysTitle') }}
            </div>
          </div>
        </div>
        <div class="assets-grid-controls">
          <button
            type="button"
            class="assets-toolbar-btn assets-toolbar-btn--danger"
            :disabled="selectedGatewayIds.length === 0"
            @click="openDeleteConfirm('gateway')"
          >
            <i class="bi bi-trash" />
            <span>{{ $t('components.infrastructure.dataCleanup.deleteSelected', { n: selectedGatewayIds.length }) }}</span>
          </button>
        </div>
      </div>
      <div class="assets-grid-card__body">
        <div class="wl-assets-search-wrap d-flex align-items-center gap-3 flex-wrap">
          <input
            v-model="gatewaySearch"
            type="text"
            class="form-control wl-assets-search"
            :placeholder="$t('components.infrastructure.dataCleanup.searchPlaceholder')"
            @input="onGatewaySearchInput"
          >
          <div class="form-check mb-0">
            <input
              id="onlyOfflineGateways"
              v-model="onlyOfflineGateways"
              type="checkbox"
              class="form-check-input"
              @change="refreshGateways"
            >
            <label
              class="form-check-label text-muted small"
              for="onlyOfflineGateways"
            >
              {{ $t('components.infrastructure.dataCleanup.onlyOffline') }}
            </label>
          </div>
        </div>

        <div
          v-if="hasActivated"
          class="wp-grid-wrap"
        >
          <div
            v-if="gatewaysLoading"
            class="wp-loading wp-loading--overlay"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.infrastructure.loading') }}</span>
          </div>

          <DxDataGrid
            ref="gatewaysGridRef"
            class="ni-grid"
            :data-source="gatewaysDataSource"
            :remote-operations="true"
            :column-auto-width="true"
            key-expr="gwId"
            :hover-state-enabled="true"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :word-wrap-enabled="false"
            :no-data-text="$t('components.infrastructure.dataCleanup.gatewaysEmpty')"
            @selection-changed="onGatewaySelectionChanged"
          >
            <DxSelection
              mode="multiple"
              show-check-boxes-mode="always"
            />
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="gwId"
              :caption="$t('components.infrastructure.gateways.columns.gwId')"
              :min-width="150"
              :allow-sorting="false"
              cell-template="gwid-template"
            />
            <DxColumn
              data-field="locationName"
              :caption="$t('components.infrastructure.gateways.columns.location')"
              :min-width="160"
              :allow-sorting="false"
            />
            <DxColumn
              data-field="state"
              :caption="$t('components.infrastructure.gateways.columns.status')"
              :min-width="110"
              alignment="center"
              :allow-sorting="false"
              cell-template="gwstatus-template"
            />
            <DxColumn
              data-field="updatedAt"
              :caption="$t('components.infrastructure.gateways.columns.updatedAt')"
              :min-width="160"
              :allow-sorting="false"
              cell-template="updated-template"
            />
            <DxColumn
              data-field="assetCount"
              :caption="$t('components.infrastructure.gateways.columns.assetCount')"
              :min-width="100"
              alignment="center"
              :allow-sorting="false"
            />

            <template #gwid-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.gwId }}</span>
            </template>

            <template #updated-template="{ data }">
              <span class="text-muted small font-monospace">{{ agoText(data.data.updatedAt) }}</span>
            </template>

            <template #gwstatus-template="{ data }">
              <span
                class="wirepas-status-badge"
                :class="data.data.state === 'online' ? 'wirepas-status-badge--online' : 'wirepas-status-badge--offline'"
              >
                <span
                  class="wirepas-status-dot"
                  :class="data.data.state === 'online' ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                />
                {{ data.data.state === 'online' ? $t('components.infrastructure.online') : $t('components.infrastructure.offline') }}
              </span>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>

    <!-- Kotwice -->
    <div class="assets-grid-card ni-grid-card--spaced">
      <div class="assets-grid-card__header">
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-broadcast" />
          </div>
          <div>
            <div class="assets-grid-title__text">
              {{ $t('components.infrastructure.dataCleanup.anchorsTitle') }}
            </div>
          </div>
        </div>
        <div class="assets-grid-controls">
          <button
            type="button"
            class="assets-toolbar-btn assets-toolbar-btn--danger"
            :disabled="selectedAnchorAddresses.length === 0"
            @click="openDeleteConfirm('anchor')"
          >
            <i class="bi bi-trash" />
            <span>{{ $t('components.infrastructure.dataCleanup.deleteSelected', { n: selectedAnchorAddresses.length }) }}</span>
          </button>
        </div>
      </div>
      <div class="assets-grid-card__body">
        <div class="wl-assets-search-wrap d-flex align-items-center gap-3 flex-wrap">
          <input
            v-model="anchorSearch"
            type="text"
            class="form-control wl-assets-search"
            :placeholder="$t('components.infrastructure.anchors.searchPlaceholder')"
            @input="onAnchorSearchInput"
          >
          <div class="form-check mb-0">
            <input
              id="onlyOfflineAnchors"
              v-model="onlyOfflineAnchors"
              type="checkbox"
              class="form-check-input"
              @change="refreshAnchors"
            >
            <label
              class="form-check-label text-muted small"
              for="onlyOfflineAnchors"
            >
              {{ $t('components.infrastructure.dataCleanup.onlyOffline') }}
            </label>
          </div>
        </div>

        <div
          v-if="hasActivated"
          class="wp-grid-wrap"
        >
          <div
            v-if="anchorsLoading"
            class="wp-loading wp-loading--overlay"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.infrastructure.loading') }}</span>
          </div>

          <DxDataGrid
            ref="anchorsGridRef"
            class="ni-grid"
            :data-source="anchorsDataSource"
            :remote-operations="true"
            :column-auto-width="true"
            key-expr="address"
            :hover-state-enabled="true"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :word-wrap-enabled="false"
            :no-data-text="$t('components.infrastructure.dataCleanup.anchorsEmpty')"
            @selection-changed="onAnchorSelectionChanged"
          >
            <DxSelection
              mode="multiple"
              show-check-boxes-mode="always"
            />
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="address"
              :caption="$t('components.infrastructure.anchors.columns.address')"
              :min-width="130"
              :allow-sorting="false"
              cell-template="addr-template"
            />
            <DxColumn
              data-field="lastSeen"
              :caption="$t('components.infrastructure.anchors.columns.lastSeen')"
              :min-width="160"
              :allow-sorting="false"
              cell-template="anchor-updated-template"
            />
            <DxColumn
              data-field="active"
              :caption="$t('components.infrastructure.anchors.columns.status')"
              :min-width="110"
              alignment="center"
              :allow-sorting="false"
              cell-template="anchor-status-template"
            />
            <DxColumn
              data-field="tagsHeard"
              :caption="$t('components.infrastructure.anchors.columns.tagsHeard')"
              :min-width="100"
              alignment="center"
              :allow-sorting="false"
            />

            <template #addr-template="{ data }">
              <span class="font-monospace small">{{ data.data.address }}</span>
            </template>

            <template #anchor-updated-template="{ data }">
              <span class="text-muted small font-monospace">{{ agoText(data.data.lastSeen) }}</span>
            </template>

            <template #anchor-status-template="{ data }">
              <span
                class="wirepas-status-badge"
                :class="data.data.active ? 'wirepas-status-badge--online' : 'wirepas-status-badge--offline'"
              >
                <span
                  class="wirepas-status-dot"
                  :class="data.data.active ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                />
                {{ data.data.active ? $t('components.infrastructure.anchors.status.active') : $t('components.infrastructure.anchors.status.silent') }}
              </span>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>

    <base-modal
      ref="confirmModalRef"
      size="md"
    >
      <template #header>
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-exclamation-triangle-fill" />
          </div>
          <div class="assets-grid-title__text">
            {{ $t('components.infrastructure.dataCleanup.confirmTitle') }}
          </div>
        </div>
      </template>

      <template #body>
        <div
          v-if="deleteError"
          class="alert alert-danger d-flex align-items-center gap-2 mb-3"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ deleteError }}</span>
        </div>
        <p>
          {{ deleteContext === 'gateway'
            ? $t('components.infrastructure.dataCleanup.confirmTextGateways', { n: selectedGatewayIds.length })
            : $t('components.infrastructure.dataCleanup.confirmTextAnchors', { n: selectedAnchorAddresses.length }) }}
        </p>
        <div
          v-if="deleteContext === 'gateway'"
          class="form-check"
        >
          <input
            id="deleteHistoryChecked"
            v-model="deleteHistoryChecked"
            type="checkbox"
            class="form-check-input"
          >
          <label
            class="form-check-label"
            for="deleteHistoryChecked"
          >
            {{ $t('components.infrastructure.dataCleanup.deleteHistory') }}
          </label>
        </div>
        <p
          v-else
          class="text-muted small mb-0"
        >
          {{ $t('components.infrastructure.dataCleanup.anchorHistoryNote') }}
        </p>
      </template>

      <template #footer>
        <div class="d-flex justify-content-between w-100">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeDeleteConfirm"
          >
            {{ $t('components.assetGroupsWithLocation.modal.close') }}
          </button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ $t('components.infrastructure.dataCleanup.confirmDelete') }}
          </button>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import CustomStore from 'devextreme/data/custom_store'
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxLoadPanel,
  DxSelection,
} from 'devextreme-vue/data-grid'
import { getWirepasGatewaysAdmin, getWirepasAnchors } from '@/crud/wirepas-location'
import { deleteWirepasGateways, deleteWirepasAnchors } from '@/crud/wirepas-location'
import BaseModal from '@/components/content/_modals/core/BaseModal.vue'
import { useDebouncedGridRefresh } from '@/composables/useDebouncedGridRefresh'
import { wirepasAgoTextFromIso } from '@/utils/wirepas'

export default defineComponent({
  name: 'WirepasDataCleanupGrid',
  components: { DxDataGrid, DxColumn, DxPaging, DxPager, DxLoadPanel, DxSelection, BaseModal },
  props: {
    active: { type: Boolean as PropType<boolean>, default: false },
  },
  setup(props) {
    const { t } = useI18n()

    const error = ref<string | null>(null)
    const hasActivated = ref(props.active)
    watch(() => props.active, (isActive) => {
      if (isActive) hasActivated.value = true
    }, { immediate: true })

    const gatewaysLoading = ref(true)
    const gatewaySearch = ref('')
    const onlyOfflineGateways = ref(true)
    const gatewaysGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null)
    const selectedGatewayIds = ref<string[]>([])

    const anchorsLoading = ref(true)
    const anchorSearch = ref('')
    const onlyOfflineAnchors = ref(true)
    const anchorsGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null)
    const selectedAnchorAddresses = ref<string[]>([])

    const gatewaysDataSource = new CustomStore({
      key: 'gwId',
      load: async (opts) => {
        gatewaysLoading.value = true
        try {
          const params: any = { skip: opts.skip ?? 0, take: opts.take ?? 10 }
          if (gatewaySearch.value.trim()) params.searchValue = gatewaySearch.value.trim()
          if (onlyOfflineGateways.value) params.onlyOffline = true
          const res = await getWirepasGatewaysAdmin(params)
          return { data: res.data?.result ?? [], totalCount: res.data?.count ?? 0 }
        } catch (e: any) {
          error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
          throw e
        } finally {
          gatewaysLoading.value = false
        }
      },
    })

    const anchorsDataSource = new CustomStore({
      key: 'address',
      load: async (opts) => {
        anchorsLoading.value = true
        try {
          const params: any = { skip: opts.skip ?? 0, take: opts.take ?? 10 }
          if (anchorSearch.value.trim()) params.searchValue = anchorSearch.value.trim()
          if (onlyOfflineAnchors.value) params.onlyOffline = true
          const res = await getWirepasAnchors(params)
          return { data: res.data?.result ?? [], totalCount: res.data?.count ?? 0 }
        } catch (e: any) {
          error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
          throw e
        } finally {
          anchorsLoading.value = false
        }
      },
    })

    const onGatewaySearchInput = useDebouncedGridRefresh(gatewaysGridRef)
    const onAnchorSearchInput = useDebouncedGridRefresh(anchorsGridRef)
    const refreshGateways = () => gatewaysGridRef.value?.instance?.refresh()
    const refreshAnchors = () => anchorsGridRef.value?.instance?.refresh()

    const onGatewaySelectionChanged = (e: any) => { selectedGatewayIds.value = e.selectedRowKeys ?? [] }
    const onAnchorSelectionChanged = (e: any) => { selectedAnchorAddresses.value = e.selectedRowKeys ?? [] }

    const confirmModalRef = ref<InstanceType<typeof BaseModal> | null>(null)
    const deleteContext = ref<'gateway' | 'anchor'>('gateway')
    const deleteHistoryChecked = ref(false)
    const deleting = ref(false)
    const deleteError = ref<string | null>(null)

    const openDeleteConfirm = (context: 'gateway' | 'anchor') => {
      deleteContext.value = context
      deleteHistoryChecked.value = false
      deleteError.value = null
      confirmModalRef.value?.openModal()
    }
    const closeDeleteConfirm = () => confirmModalRef.value?.closeModal()

    const confirmDelete = async () => {
      deleting.value = true
      deleteError.value = null
      try {
        if (deleteContext.value === 'gateway') {
          await deleteWirepasGateways(selectedGatewayIds.value, deleteHistoryChecked.value)
          selectedGatewayIds.value = []
          gatewaysGridRef.value?.instance?.clearSelection()
          refreshGateways()
        } else {
          await deleteWirepasAnchors(selectedAnchorAddresses.value)
          selectedAnchorAddresses.value = []
          anchorsGridRef.value?.instance?.clearSelection()
          refreshAnchors()
        }
        closeDeleteConfirm()
      } catch (e: any) {
        deleteError.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
      } finally {
        deleting.value = false
      }
    }

    const agoText = (iso: string | null) => wirepasAgoTextFromIso(iso, t)

    return {
      error,
      hasActivated,
      gatewaysLoading,
      gatewaySearch,
      onlyOfflineGateways,
      gatewaysGridRef,
      gatewaysDataSource,
      selectedGatewayIds,
      anchorsLoading,
      anchorSearch,
      onlyOfflineAnchors,
      anchorsGridRef,
      anchorsDataSource,
      selectedAnchorAddresses,
      onGatewaySearchInput,
      onAnchorSearchInput,
      refreshGateways,
      refreshAnchors,
      onGatewaySelectionChanged,
      onAnchorSelectionChanged,
      confirmModalRef,
      deleteContext,
      deleteHistoryChecked,
      deleting,
      deleteError,
      openDeleteConfirm,
      closeDeleteConfirm,
      confirmDelete,
      agoText,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';

// !important throughout: a global dark-mode rule otherwise wins and repaints this white in dark mode.
.assets-toolbar-btn--danger {
  color: #ac4c3a !important;
  border-color: rgba(172, 76, 58, 0.4) !important;

  i { color: #ac4c3a !important; }

  &:hover:not(:disabled) {
    background: #ac4c3a !important;
    color: #fff !important;

    i { color: #fff !important; }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Bootstrap's .form-check-input checks in blue by default -- match the app's orange instead.
.form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.form-check-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.25);
}

input[type="checkbox"] {
  accent-color: var(--primary-color);
}
</style>
